import { ensureFonts, loadPhoto } from "./badgeCanvas"

export { loadPhoto }
export const BADGE_W = 1440
export const BADGE_H = 1080
export const PHOTO_FRAME = { x: 86, y: 116, width: 476, height: 527, radius: 65 }
export const BADGE_TEMPLATES = [
  { id: "twilight", name: "Vizag Twilight", background: "/badge/vizag-twilight-template.png", thumbnail: "/Vizag Twilight Presentation Badge.png", photoFrame: PHOTO_FRAME },
  { id: "gold", name: "Navy & Gold", background: "/badge/vizag-gold-template.png", thumbnail: "/CTS 2026 Vizag Presenter Badge.png", photoFrame: { x: 846, y: 81, width: 506, height: 538, radius: 45 } },
]
export const getBadgeTemplate = (id) => BADGE_TEMPLATES.find((template) => template.id === id) || BADGE_TEMPLATES[0]
export const BADGE_ASSETS = {
  cts: "/badge/cts-vizag-transparent.png",
  bg: "/badge/vizag-twilight-template.png",
  livestreamQr: "/yt live stream QR.jpeg",
}

export async function prepareBadge(templateId = "twilight") {
  const [cts, bg, livestreamQr] = await Promise.all([
    loadPhoto(BADGE_ASSETS.cts),
    loadPhoto(getBadgeTemplate(templateId).background),
    loadPhoto(BADGE_ASSETS.livestreamQr),
  ])
  await ensureFonts()
  if (!cts || !bg || !livestreamQr) throw new Error("Could not load the Vizag badge artwork")
  return { cts, bg, livestreamQr }
}

// Shared by the portrait editor and export. Offsets are fractions of each axis.
export function photoPlacement(image, boxWidth, zoom = 1, posX = 0, posY = 0, boxHeight = boxWidth) {
  const scale = Math.max(boxWidth / image.width, boxHeight / image.height) * zoom
  const width = image.width * scale
  const height = image.height * scale
  const offset = (position, dimension, box) => Math.max(-(dimension - box) / 2, Math.min((dimension - box) / 2, position * box))
  return { width, height, x: (boxWidth - width) / 2 + offset(posX, width, boxWidth), y: (boxHeight - height) / 2 + offset(posY, height, boxHeight) }
}

function roundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

function text(ctx, value, x, baseline, maxWidth, size, { color = "#fff", align = "left", weight = 900, shadow = true, family = "Archivo, Arial, sans-serif", italic = false, outline = null } = {}) {
  ctx.save()
  ctx.font = `${italic ? "italic " : ""}${weight} ${size}px ${family}`
  while (ctx.measureText(value).width > maxWidth && size > 12) {
    size--
    ctx.font = `${italic ? "italic " : ""}${weight} ${size}px ${family}`
  }
  ctx.textAlign = align
  ctx.textBaseline = "alphabetic"
  ctx.fillStyle = color
  if (shadow) {
    ctx.shadowColor = "#00051be6"
    ctx.shadowOffsetY = 4
    ctx.shadowBlur = 6
  }
  if (outline) { ctx.strokeStyle = outline; ctx.lineWidth = 1; ctx.strokeText(value, x, baseline) }
  ctx.fillText(value, x, baseline)
  ctx.restore()
}

function sessionLines(ctx, value) {
  // Break even unspaced titles, and keep the optional session inside two lines.
  const words = value.trim().split(/\s+/)
  let size = 30
  while (size >= 18) {
    ctx.font = `700 ${size}px Archivo, Arial, sans-serif`
    const lines = []
    let line = ""
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word
      if (ctx.measureText(candidate).width <= 672) {
        line = candidate
        continue
      }
      if (line) lines.push(line)
      line = ""
      for (const char of Array.from(word)) {
        if (ctx.measureText(line + char).width > 672) { lines.push(line); line = "" }
        line += char
      }
    }
    if (line) lines.push(line)
    if (lines.length <= 2) return { lines, size }
    size--
  }
  return { lines: [value], size: 18 }
}

export function drawBadge(ctx, { name = "", jobTitle = "", company = "", talk = "", photoImg, zoom = 1, posX = 0, posY = 0, variant = "attending", templateId = "twilight" }, { bg, livestreamQr }) {
  ctx.save()
  ctx.clearRect(0, 0, BADGE_W, BADGE_H)
  // Prepared backgrounds retain each supplied logo, frames and coastline.
  ctx.drawImage(bg, 0, 0, BADGE_W, BADGE_H)
  const isGold = templateId === "gold"

  const silver = ctx.createLinearGradient(0, 290, 0, 385)
  silver.addColorStop(0, "#ffffff")
  silver.addColorStop(.45, "#f4f6ff")
  silver.addColorStop(1, "#a9b5d5")
  const gold = ctx.createLinearGradient(0, isGold ? 350 : 391, 0, isGold ? 470 : 475)
  gold.addColorStop(0, "#fff9aa")
  gold.addColorStop(.34, "#ffe65d")
  gold.addColorStop(.65, "#ffc600")
  gold.addColorStop(1, "#df9300")
  const headline = { family: "'Times New Roman', Times, serif", italic: !isGold, weight: 700, align: "center" }
  text(ctx, "I’M", isGold ? 432 : 1000, isGold ? 464 : 378, 730, isGold ? 144 : 124, { ...headline, color: isGold ? gold : silver, outline: isGold ? "#ffe866" : "#edf3ff" })
  text(ctx, variant === "presenting" ? "PRESENTING" : "ATTENDING", isGold ? 432 : 1000, isGold ? 568 : 473, isGold ? 730 : 740, isGold ? 118 : 110, { ...headline, color: isGold ? "#fffdf2" : gold, outline: isGold ? "#fffdf2" : "#ffe866" })

  const frame = getBadgeTemplate(templateId).photoFrame
  if (photoImg) {
    ctx.save()
    roundedRect(ctx, frame.x, frame.y, frame.width, frame.height, frame.radius)
    ctx.clip()
    const crop = photoPlacement(photoImg, frame.width, zoom, posX, posY, frame.height)
    ctx.drawImage(photoImg, frame.x + crop.x, frame.y + crop.y, crop.width, crop.height)
    ctx.restore()
  } else {
    // Scale the placeholder with the selected frame, just like the crop editor.
    ctx.save()
    ctx.translate(frame.x, frame.y)
    ctx.scale(frame.width / PHOTO_FRAME.width, frame.height / PHOTO_FRAME.height)
    ctx.translate(-PHOTO_FRAME.x, -PHOTO_FRAME.y)
    ctx.fillStyle = "#355a8188"
    ctx.beginPath()
    ctx.arc(324, 304, 83, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()
    ctx.moveTo(181, 498)
    ctx.bezierCurveTo(181, 358, 467, 358, 467, 498)
    ctx.quadraticCurveTo(467, 510, 455, 510)
    ctx.lineTo(193, 510)
    ctx.quadraticCurveTo(181, 510, 181, 498)
    ctx.fill()
    text(ctx, "ADD YOUR PHOTO", 324, 570, 443, 40, { align: "center", color: "#051440", shadow: false })
    ctx.restore()
  }

  const detail = [jobTitle.trim(), company.trim()].filter(Boolean).join(" • ")
  const nameX = isGold ? 1108 : 335
  const nameY = isGold ? 713 : 743
  text(ctx, (name.trim() || "YOUR NAME").toLocaleUpperCase(), nameX, detail ? nameY - 23 : nameY, 486, detail ? 44 : 60, { align: "center", family: "Georgia, 'Times New Roman', serif", weight: 700, color: "#f7f9ff" })
  if (detail) text(ctx, detail, nameX, nameY + 8, 486, 21, { align: "center", weight: 700, color: "#d8e8ff" })

  // Compact bottom-left footer, aligned with the QR image's bottom edge.
  roundedRect(ctx, 62, 954, 540, 64, 12)
  ctx.fillStyle = "#050c3aeb"
  ctx.fill()
  ctx.strokeStyle = "#f4cd63"
  ctx.lineWidth = 2
  ctx.stroke()
  text(ctx, "Nov 5th  •  From 9:30 AM", 332, 997, 492, 32, { align: "center", color: "#fff5c5", weight: 800, shadow: false })

  if (variant === "presenting" && talk.trim()) {
    const sessionX = isGold ? 62 : 638
    const sessionY = isGold ? 766 : 630
    roundedRect(ctx, sessionX, sessionY, 730, 112, 16)
    ctx.fillStyle = "#050c3ae8"
    ctx.fill()
    text(ctx, "MY SESSION", sessionX + 24, sessionY + 27, 672, 18, { color: "#ffe38c", weight: 800 })
    const { lines, size } = sessionLines(ctx, talk)
    lines.forEach((line, index) => text(ctx, line, sessionX + 24, sessionY + 61 + index * 34, 672, size, { weight: 700 }))
  }

  // Keep the supplied QR and its white quiet zone intact in every export.
  roundedRect(ctx, 1096, 756, 274, 306, 16)
  ctx.fillStyle = "#050c3af5"
  ctx.fill()
  ctx.strokeStyle = "#f4cd63"
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(livestreamQr, 1108, 768, 250, 250)
  text(ctx, "YOUTUBE LIVE", 1233, 1044, 250, 20, { align: "center", color: "#ffe38c", weight: 800, shadow: false })
  ctx.restore()
}
